import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Drawer, Input, Space, Tag, Typography, theme } from "antd";
import {
  AudioOutlined,
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  SendOutlined,
  SoundOutlined,
  StopOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Error as ErrorMessage } from "../../../commons/Messages/Messages";
import { useIsCompact } from "../../../design-system/useMediaQuery";
import useDictation from "../measurements/useDictation";
import { speechLanguage } from "../measurements/format";
import * as api from "./api";
import "./Assistant.css";

// Short spoken answers to a pending proposal, handled here without asking the assistant.
const YES =
  /^(ja|jawohl|jep|ok(ay)?|bestätigen|bestätige|mach( das)?|yes|yeah|yep|confirm|do it|evet|tamam|onayla|نعم|أكد|تمام)[.!]?$/i;
const NO = /^(nein|nee|abbrechen|verwerfen|no|nope|cancel|hayır|iptal|لا|إلغاء)[.!]?$/i;

const speak = (text, language) => {
  try {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = speechLanguage(language);
    window.speechSynthesis.speak(utterance);
  } catch {
    // reading aloud is a convenience only
  }
};

const readSetting = key => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeSetting = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const ActionCard = ({ action, onChange }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [busy, setBusy] = useState(null);

  const run = async (kind, force = false) => {
    setBusy(kind);
    try {
      const next =
        kind === "dismiss"
          ? await api.dismissAction(action.id)
          : await api.confirmAction(action.id, force);
      onChange(next);
    } catch (error) {
      ErrorMessage(api.errorText(error, t("The action could not be executed.")));
      onChange({ ...action, status: "failed", error: api.errorText(error, "") });
    } finally {
      setBusy(null);
    }
  };

  const open = ["pending", "conflict"].includes(action.status);
  return (
    <div className={`assistant-card assistant-card--${action.status}`}>
      <div className="assistant-card__label">{action.label}</div>
      {action.details?.map(line => (
        <div key={line} className="assistant-card__detail">
          {line}
        </div>
      ))}
      {action.status === "conflict" && (
        <div className="assistant-card__warning">
          {(Array.isArray(action.conflicts) ? action.conflicts : []).map((c, i) => (
            <div key={i}>
              {[c.employeeName, c.date, c.detail || c.type].filter(Boolean).join(" · ")}
            </div>
          ))}
        </div>
      )}
      {action.status === "executed" && <Tag color="success">{t("Done")}</Tag>}
      {action.status === "dismissed" && <Tag>{t("Dismissed")}</Tag>}
      {action.status === "failed" && <Tag color="error">{action.error || t("Failed")}</Tag>}
      <Space wrap className="assistant-card__buttons">
        {action.status === "pending" && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={busy === "confirm"}
            disabled={Boolean(busy)}
            onClick={() => run("confirm")}
          >
            {t("Confirm")}
          </Button>
        )}
        {action.status === "conflict" && (
          <Button
            type="primary"
            danger
            loading={busy === "confirm"}
            disabled={Boolean(busy)}
            onClick={() => run("confirm", true)}
          >
            {t("Do it anyway")}
          </Button>
        )}
        {open && (
          <Button
            icon={<CloseOutlined />}
            loading={busy === "dismiss"}
            disabled={Boolean(busy)}
            onClick={() => run("dismiss")}
          >
            {t("Dismiss")}
          </Button>
        )}
        {action.status === "executed" && action.link && (
          <Button onClick={() => history.push(action.link)}>{t("Open")}</Button>
        )}
      </Space>
    </div>
  );
};

/**
 * The assistant: a microphone button on every /user page. Say or type what should happen; the
 * assistant looks things up and prepares changes, which run only after "Confirm" (or a spoken
 * "ja"). It acts with the user's own rights.
 */
const AssistantWidget = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const history = useHistory();
  const location = useLocation();
  const compact = useIsCompact();
  const language = (i18n.language || "de").slice(0, 2);

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [readAloud, setReadAloud] = useState(readSetting("assistant.readAloud") !== "0");
  const sendAfterStop = useRef(false);
  const heard = useRef("");
  const bottom = useRef(null);

  useEffect(() => {
    api
      .getStatus()
      .then(setStatus)
      .catch(() => setStatus({ configured: false }));
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages, busy]);

  const pendingActions = () =>
    messages.flatMap(m => m.actions || []).filter(a => a.status === "pending");

  const updateAction = next =>
    setMessages(list =>
      list.map(m =>
        m.actions ? { ...m, actions: m.actions.map(a => (a.id === next.id ? next : a)) } : m
      )
    );

  const send = async (raw, source) => {
    const command = String(raw || "").trim();
    if (!command || busy) return;
    setText("");
    // "ja" / "nein" answer the latest open proposal directly
    const pending = pendingActions();
    if (pending.length && (YES.test(command) || NO.test(command))) {
      const target = pending[pending.length - 1];
      setMessages(list => [...list, { role: "user", content: command }]);
      try {
        const next = YES.test(command)
          ? await api.confirmAction(target.id)
          : await api.dismissAction(target.id);
        updateAction(next);
        if (source === "voice" && readAloud)
          speak(
            next.status === "executed"
              ? t("Done")
              : next.status === "conflict"
                ? t("There are warnings. Please check.")
                : t("Dismissed"),
            language
          );
      } catch (error) {
        ErrorMessage(api.errorText(error, t("The action could not be executed.")));
      }
      return;
    }
    const past = messages.filter(m => m.content).map(m => ({ role: m.role, content: m.content }));
    setMessages(list => [...list, { role: "user", content: command }]);
    setBusy(true);
    try {
      const result = await api.sendCommand({
        text: command,
        history: past,
        language,
        source,
        page: location.pathname + location.search
      });
      setMessages(list => [
        ...list,
        { role: "assistant", content: result.reply, actions: result.actions || [] }
      ]);
      if (source === "voice" && readAloud) speak(result.reply, language);
      if (result.navigate) {
        history.push(result.navigate);
        if (compact) setOpen(false);
      }
      setStatus(s => (s && result.quota ? { ...s, quota: { ...s.quota, ...result.quota } } : s));
    } catch (error) {
      setMessages(list => [
        ...list,
        {
          role: "assistant",
          content: "",
          error: api.errorText(error, t("The assistant is not available right now."))
        }
      ]);
    } finally {
      setBusy(false);
    }
  };

  const dictation = useDictation({
    language,
    serverAvailable: Boolean(status?.voiceTranscription),
    transcribe: api.transcribeAudio,
    onText: piece => {
      heard.current = `${heard.current} ${piece}`.trim();
      setText(heard.current);
    },
    onError: code => {
      sendAfterStop.current = false;
      const errors = {
        microphone: "Please allow access to the microphone.",
        empty: "Nothing was understood. Please try again.",
        unsupported: "Voice input is not available on this device.",
        live: "Speech recognition stopped. Please try again.",
        server: "The recording could not be processed."
      };
      ErrorMessage(t(errors[code] || errors.live));
    }
  });

  const listening = dictation.status === "listening" || dictation.status === "recording";

  // Send what was heard once the microphone is off and the last words are in
  useEffect(() => {
    if (dictation.status === "idle" && sendAfterStop.current) {
      sendAfterStop.current = false;
      const said = heard.current;
      heard.current = "";
      if (said) send(said, "voice");
    }
  }, [dictation.status]);

  const toggleMic = () => {
    if (listening) {
      sendAfterStop.current = true;
      dictation.stop();
      return;
    }
    window.speechSynthesis?.cancel?.();
    heard.current = "";
    setText("");
    dictation.start();
  };

  const startFromFab = () => {
    setOpen(true);
    if (dictation.available && !listening) toggleMic();
  };

  if (!status?.configured) return null;

  const quotaLeft = status.quota?.limit
    ? Math.max(0, status.quota.limit - (status.quota.used || 0))
    : null;

  return (
    <>
      <button
        type="button"
        className={`assistant-fab${listening ? " assistant-fab--live" : ""}`}
        aria-label={t("Assistant")}
        title={t("Assistant")}
        onClick={startFromFab}
      >
        <AudioOutlined />
      </button>
      <Drawer
        rootClassName="assistant-sheet"
        placement={compact ? "bottom" : "right"}
        width={compact ? undefined : 420}
        height={compact ? "80vh" : undefined}
        open={open}
        onClose={() => {
          if (listening) dictation.stop();
          sendAfterStop.current = false;
          setOpen(false);
        }}
        title={
          <Space size={8}>
            {t("Assistant")}
            {quotaLeft !== null && (
              <Typography.Text type="secondary" className="assistant-quota">
                {t("{{count}} commands left this month", { count: quotaLeft })}
              </Typography.Text>
            )}
          </Space>
        }
        extra={
          <Button
            type="text"
            aria-pressed={readAloud}
            title={t("Read answers aloud")}
            icon={
              <SoundOutlined
                style={{ color: readAloud ? token.colorPrimary : token.colorTextQuaternary }}
              />
            }
            onClick={() => {
              writeSetting("assistant.readAloud", readAloud ? "0" : "1");
              setReadAloud(!readAloud);
              if (readAloud) window.speechSynthesis?.cancel?.();
            }}
          />
        }
        styles={{ body: { display: "flex", flexDirection: "column", padding: 0 } }}
      >
        <div className="assistant-log">
          {!messages.length && (
            <div className="assistant-empty">
              <Typography.Paragraph type="secondary">
                {t("Say or type what should happen. Changes are only made after you confirm them.")}
              </Typography.Paragraph>
              <Space direction="vertical" size={6}>
                {[
                  t("What appointments do I have tomorrow?"),
                  t("Which invoices are overdue?"),
                  t("Clock me in"),
                  t("Create an appointment with Müller tomorrow at 9")
                ].map(example => (
                  <Button key={example} size="small" onClick={() => send(example, "text")}>
                    {example}
                  </Button>
                ))}
              </Space>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`assistant-msg assistant-msg--${m.role}`}>
              {m.content && <div className="assistant-bubble">{m.content}</div>}
              {m.error && <div className="assistant-bubble assistant-bubble--error">{m.error}</div>}
              {m.actions?.map(a => (
                <ActionCard key={a.id} action={a} onChange={updateAction} />
              ))}
            </div>
          ))}
          {busy && (
            <div className="assistant-msg assistant-msg--assistant">
              <div className="assistant-bubble">
                <LoadingOutlined /> {t("Working on it …")}
              </div>
            </div>
          )}
          <div ref={bottom} />
        </div>
        <div className="assistant-input">
          {dictation.status === "listening" && (
            <div className="assistant-interim">
              <span className="assistant-rec-dot" /> {dictation.interim || t("Listening…")}
            </div>
          )}
          {dictation.status === "recording" && (
            <div className="assistant-interim">
              <span className="assistant-rec-dot" /> {t("Recording")} ·{" "}
              {t("Tap stop when you are done.")}
            </div>
          )}
          <div className="assistant-input__row">
            {dictation.available && (
              <Button
                shape="circle"
                size="large"
                type={listening ? "primary" : "default"}
                danger={listening}
                aria-label={listening ? t("Stop") : t("Speak")}
                icon={
                  dictation.status === "processing" || dictation.status === "starting" ? (
                    <LoadingOutlined />
                  ) : listening ? (
                    <StopOutlined />
                  ) : (
                    <AudioOutlined />
                  )
                }
                disabled={busy || dictation.status === "processing"}
                onClick={toggleMic}
              />
            )}
            <Input.TextArea
              value={text}
              onChange={e => setText(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={2000}
              placeholder={t("e.g. Which invoices are overdue?")}
              onPressEnter={e => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  send(text, "text");
                }
              }}
            />
            <Button
              type="primary"
              shape="circle"
              size="large"
              aria-label={t("Send")}
              icon={<SendOutlined />}
              loading={busy}
              disabled={!text.trim() || listening}
              onClick={() => send(text, "text")}
            />
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AssistantWidget;
